import { useEffect, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import type { AccessGroupSize, AccessUse } from '../../../shared/protocol.ts';
import { account } from '../lib/account.ts';
import type { Observable } from '../lib/emitter.ts';

export function useObservable<T extends Observable>(o: T | null | undefined): T | null | undefined {
  useSyncExternalStore(o ? o.subscribe : noopSubscribe, o ? o.getVersion : zero);
  return o;
}
const noopSubscribe = () => () => {};
const zero = () => 0;

export function useInterval(fn: () => void, ms: number) {
  const ref = useRef(fn);
  ref.current = fn;
  useEffect(() => {
    const t = setInterval(() => ref.current(), ms);
    return () => clearInterval(t);
  }, [ms]);
}

export function useNow(ms = 500) {
  const [now, setNow] = useState(Date.now());
  useInterval(() => setNow(Date.now()), ms);
  return now;
}

const PATHS: Record<string, string> = {
  mic: 'M12 3a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V6a3 3 0 0 0-3-3Zm-7 9a7 7 0 0 0 14 0M12 19v3',
  micOff: 'M3 3l18 18M9 9v3a3 3 0 0 0 5.1 2.1M15 9.3V6a3 3 0 0 0-5.7-1.3M5 12a7 7 0 0 0 11.6 5.3M19 12a7 7 0 0 1-.4 2.3M12 19v3',
  cam: 'M15 10l5-3v10l-5-3M3 7h12v10H3z',
  camOff: 'M3 3l18 18M15 10l5-3v10l-5-3M10 7h5v5M3 7h2m-2 0v10h12v-2',
  screen: 'M3 4h18v12H3zM8 20h8M12 16v4',
  plus: 'M12 5v14M5 12h14',
  replay: 'M3 12a9 9 0 1 0 3-6.7L3 8M3 3v5h5',
  rec: 'M12 12m-6 0a6 6 0 1 0 12 0a6 6 0 1 0 -12 0',
  stop: 'M7 7h10v10H7z',
  leave: 'M15 4h4v16h-4M10 8l-4 4 4 4M6 12h11',
  upload: 'M12 16V4M7 9l5-5 5 5M4 20h16',
  check: 'M5 12l5 5 9-10',
  warn: 'M12 3l10 18H2zM12 10v5M12 18v.5',
  flag: 'M5 21V4h11l-2 4 2 4H5',
  gear: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Zm7.4-3a7.4 7.4 0 0 0-.1-1.2l2-1.6-2-3.4-2.4 1a7.5 7.5 0 0 0-2-1.2L14.5 3h-4l-.4 2.6a7.5 7.5 0 0 0-2 1.2l-2.4-1-2 3.4 2 1.6a7.4 7.4 0 0 0 0 2.4l-2 1.6 2 3.4 2.4-1a7.5 7.5 0 0 0 2 1.2l.4 2.6h4l.4-2.6a7.5 7.5 0 0 0 2-1.2l2.4 1 2-3.4-2-1.6c.1-.4.1-.8.1-1.2Z',
  users: 'M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm-7 10a7 7 0 0 1 14 0M17 3a4 4 0 0 1 0 8M22 21a7 7 0 0 0-4-6.3',
  layers: 'M12 3l9 5-9 5-9-5zM3 13l9 5 9-5',
  download: 'M12 4v12M7 11l5 5 5-5M4 20h16',
  close: 'M6 6l12 12M18 6L6 18',
  trash: 'M4 7h16M10 11v6M14 11v6M6 7l1 13h10l1-13M9 7V4h6v3',
  link: 'M10 14a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1 1M14 10a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1-1',
  eye: 'M2 12s4-7 10-7 10 7 10 7-4 7-10 7S2 12 2 12Zm10 3a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z',
  eyeOff: 'M3 3l18 18M10.6 5.1A10 10 0 0 1 12 5c6 0 10 7 10 7a17 17 0 0 1-3 3.7M6.6 6.6A17 17 0 0 0 2 12s4 7 10 7a10 10 0 0 0 4.4-1',
  folder: 'M3 6h6l2 2h10v11H3z',
  ban: 'M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0M5.6 5.6l12.8 12.8',
  copy: 'M9 9h11v11H9zM5 15H4V4h11v1',
  headphones: 'M4 16v-4a8 8 0 0 1 16 0v4M4 15h3v5H4zM17 15h3v5h-3z',
  speaker: 'M4 9h4l5-4v14l-5-4H4zM17 9a4 4 0 0 1 0 6',
  info: 'M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0M12 11v5M12 8v.5',
  arrowLeft: 'M19 12H5M11 6l-6 6 6 6',
  external: 'M14 4h6v6M20 4l-9 9M18 14v6H4V6h6',
  clock: 'M12 12m-9 0a9 9 0 1 0 18 0a9 9 0 1 0 -18 0M12 7v5l3 2',
  wave: 'M3 12h2M7 8v8M11 5v14M15 9v6M19 11v2',
  timeline: 'M3 6h10M7 12h14M3 18h8',
  shield: 'M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z',
  user: 'M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8Zm-7 10a7 7 0 0 1 14 0',
  panel: 'M3 4h18v16H3zM15 4v16',
  film: 'M3 4h18v16H3zM7 4v16M17 4v16M3 9h4M3 15h4M17 9h4M17 15h4',
  fileText: 'M6 3h9l4 4v14H6zM14 3v5h5M9 13h7M9 17h5',
  host: 'M12 3l2.5 5 5.5.8-4 3.9.9 5.5L12 15.6 7.1 18.2 8 12.7 4 8.8 9.5 8z',
  edit: 'M4 20h4L19 9l-4-4L4 16zM13.5 6.5l4 4',
  play: 'M7 4l13 8-13 8z',
  chat: 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z',
  more: 'M5 12h.01M12 12h.01M19 12h.01',
};

export function Icon({ name, size = 18 }: { name: keyof typeof PATHS | string; size?: number }) {
  const fill = name === 'rec' || name === 'play';
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={fill ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d={PATHS[name] ?? ''} />
    </svg>
  );
}

let audioCtx: AudioContext | null = null;
export const sharedAudioContext = () => (audioCtx ??= new AudioContext());

/** Live input level for a mic track (0..1, perceptual). */
export function useLevel(track: MediaStreamTrack | null | undefined) {
  const [level, setLevel] = useState(0);
  useEffect(() => {
    if (!track || track.readyState !== 'live') return;
    const ctx = sharedAudioContext();
    void ctx.resume();
    const src = ctx.createMediaStreamSource(new MediaStream([track]));
    const analyser = ctx.createAnalyser();
    analyser.fftSize = 1024;
    src.connect(analyser);
    const buf = new Float32Array(analyser.fftSize);
    let raf = 0;
    let smooth = 0;
    const tick = () => {
      analyser.getFloatTimeDomainData(buf);
      let peak = 0;
      for (const v of buf) peak = Math.max(peak, Math.abs(v));
      const db = 20 * Math.log10(peak || 1e-6);
      const norm = Math.max(0, Math.min(1, (db + 60) / 60));
      smooth = Math.max(norm, smooth * 0.9);
      setLevel(smooth);
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => {
      cancelAnimationFrame(raf);
      src.disconnect();
    };
  }, [track]);
  return level;
}

export function Meter({ track }: { track?: MediaStreamTrack | null }) {
  const level = useLevel(track);
  return (
    <div className="meter" title="Input level">
      <i style={{ width: `${Math.round(level * 100)}%` }} />
    </div>
  );
}

export function VideoEl({ stream, track, muted = true, mirror, className }: { stream?: MediaStream | null; track?: MediaStreamTrack | null; muted?: boolean; mirror?: boolean; className?: string }) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const s = stream ?? (track ? new MediaStream([track]) : null);
    if (el.srcObject !== s) el.srcObject = s;
    if (s) void el.play().catch(() => {});
  }, [stream, track]);
  return <video ref={ref} autoPlay playsInline muted={muted} className={[className, mirror ? 'mirror' : ''].join(' ')} />;
}

export function AudioEl({ track }: { track?: MediaStreamTrack | null }) {
  const ref = useRef<HTMLAudioElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.srcObject = track ? new MediaStream([track]) : null;
    if (track) void el.play().catch(() => {});
  }, [track]);
  return <audio ref={ref} autoPlay />;
}

export function Menu({ label, children, className, down, title }: { label: ReactNode; children: (close: () => void) => ReactNode; className?: string; down?: boolean; title?: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);
  return (
    <div className={`menu ${down ? 'down' : ''}`} ref={ref}>
      <button className={className} onClick={() => setOpen(!open)} aria-expanded={open} title={title}>
        {label}
      </button>
      {open && <div className="popover">{children(() => setOpen(false))}</div>}
    </div>
  );
}

export function CopyField({ value, label }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="copyfield">
      <input readOnly value={value} onFocus={(e) => e.currentTarget.select()} aria-label={label} />
      <button
        onClick={async () => {
          await navigator.clipboard.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
      >
        <Icon name={copied ? 'check' : 'copy'} size={15} /> {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

/** The Wordstow mark: a record light. Never a link, so it can't pull anyone out of a take. */
export function Logo({ compact }: { compact?: boolean }) {
  return (
    <span className="logo" aria-label="Wordstow">
      <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden>
        <rect x="1.75" y="1.75" width="20.5" height="20.5" rx="5.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
        <circle cx="12" cy="12" r="4.25" fill="var(--rec)" />
      </svg>
      {!compact && <span>Wordstow</span>}
    </span>
  );
}

/** Top bar for the pages outside the live room. */
export function AppBar({ children }: { children?: ReactNode }) {
  return (
    <header className="appbar">
      <Logo />
      {children}
      {!children && <span className="grow" />}
      <a className="btn sm ghost" href="/local" title="Recordings kept in this browser, and copying them out">
        <Icon name="folder" size={15} /> <span className="hide-narrow">On this computer</span>
      </a>
      <AccountMenu />
    </header>
  );
}

/**
 * Asks before something that can't be undone. With `typeToConfirm`, the button only works once that text is
 * typed (for the biggest deletions).
 */
export function ConfirmDialog({
  title,
  children,
  confirmLabel,
  typeToConfirm,
  onConfirm,
  onClose,
}: {
  title: string;
  children: ReactNode;
  confirmLabel: string;
  typeToConfirm?: string;
  onConfirm: () => Promise<void>;
  onClose: () => void;
}) {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const ready = !typeToConfirm || typed.trim() === typeToConfirm.trim();
  const go = async () => {
    if (!ready || busy) return;
    setBusy(true);
    setError('');
    try {
      await onConfirm();
      onClose();
    } catch (e) {
      setError((e as Error).message);
      setBusy(false);
    }
  };
  return (
    <div className="modal-back" onClick={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="modal confirm" role="alertdialog" aria-label={title}>
        <header>
          <span className="ficon danger-icon">
            <Icon name="warn" size={17} />
          </span>
          <h2 className="grow">{title}</h2>
        </header>
        <div className="content">
          <div className="col" style={{ gap: 10 }}>{children}</div>
          {typeToConfirm && (
            <label className="field">
              <span>
                Type <strong>{typeToConfirm}</strong> to confirm
              </span>
              <input value={typed} autoFocus onChange={(e) => setTyped(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && void go()} />
            </label>
          )}
          {error && (
            <div className="alert bad">
              <Icon name="warn" size={16} /> {error}
            </div>
          )}
        </div>
        <footer>
          <span className="grow" />
          <button onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="danger-solid" onClick={() => void go()} disabled={!ready || busy} autoFocus={!typeToConfirm}>
            {busy && <span className="spinner" />} {confirmLabel}
          </button>
        </footer>
      </div>
    </div>
  );
}

/**
 * Who is signed in on this browser, with the way to their studios, their account and signing out. In the live
 * room (`inRoom`) it shows only a face, opens pages in a new tab and offers no sign-out, so nothing in it can
 * take anyone out of a call.
 */
export function AccountMenu({ inRoom = false }: { inRoom?: boolean } = {}) {
  useObservable(account);
  const me = account.account;
  if (!me) {
    if (location.pathname === '/') return null;
    return (
      <a className="btn sm ghost" href="/">
        Sign in
      </a>
    );
  }
  const label = me.name || me.email;
  return (
    <Menu
      down
      className="ghost sm account-button"
      title={me.email}
      label={
        <>
          <Avatar name={label} size={24} />
          {!inRoom && (
            <span className="ellipsis hide-narrow" style={{ maxWidth: 200 }}>
              {me.email}
            </span>
          )}
        </>
      }
    >
      {(close) => (
        <>
          <div className="menu-label col" style={{ gap: 0, paddingBottom: 6 }}>
            <strong style={{ fontSize: 13 }}>{me.name || 'Signed in'}</strong>
            <span className="faint small">{me.email}</span>
          </div>
          <button onClick={() => (close(), inRoom ? window.open('/', '_blank') : (location.href = '/'))}>
            Your studios{inRoom ? ' ↗' : ''}
            <span className="hint">Every studio and recording tied to this email</span>
          </button>
          <button onClick={() => (close(), inRoom ? window.open('/account', '_blank') : (location.href = '/account'))}>
            Account{inRoom ? ' ↗' : ''}
            <span className="hint">Your name, and signing out on other devices</span>
          </button>
          {!inRoom && (
            <button
              onClick={async () => {
                close();
                await account.signOut();
                location.href = '/';
              }}
            >
              Sign out
              <span className="hint">Recordings stay safe; sign in again with the same email</span>
            </button>
          )}
        </>
      )}
    </Menu>
  );
}

const ROLE_LABEL: Record<string, string> = { host: 'Host', producer: 'Producer', guest: 'Guest' };
export const roleLabel = (role?: string | null) => (role ? (ROLE_LABEL[role] ?? role) : '');

/** What someone registering interest in creating studios will record, and with how many people. */
export const ACCESS_USES: [AccessUse, string][] = [
  ['podcast', 'Podcast'],
  ['business', 'Interviews or business'],
  ['gaming', 'Gaming or streams'],
  ['other', 'Something else'],
];
export const ACCESS_SIZES: [AccessGroupSize, string][] = [
  ['1', 'Just me'],
  ['2', '2 people'],
  ['3-4', '3–4 people'],
  ['5+', '5 or more'],
];

export function initials(name: string) {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((s) => s[0]?.toUpperCase())
      .join('') || '?'
  );
}

/** Initials on a colour picked from the name, so each person keeps the same colour everywhere. */
export function Avatar({ name, size = 28 }: { name: string; size?: number }) {
  let h = 0;
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) % 360;
  return (
    <span className="avatar" style={{ width: size, height: size, fontSize: Math.round(size * 0.38), background: `hsl(${h} 18% 32%)` }} aria-hidden>
      {initials(name)}
    </span>
  );
}

/** Sets the browser tab title while a page is shown. */
export function useTitle(title: string) {
  useEffect(() => {
    document.title = title ? `${title} · Wordstow` : 'Wordstow';
  }, [title]);
}

/** Small badge describing a mute state – amber = not sent but recorded, red = not recorded. */
export function MuteBadge({ kind, mute, compact }: { kind: string; mute: 'none' | 'send' | 'hard'; compact?: boolean }) {
  if (mute === 'none') return null;
  const what = kind === 'microphone' ? 'Mic' : kind === 'camera' ? 'Camera' : kind === 'system-audio' ? 'Sound' : 'Screen';
  const icon = kind === 'microphone' || kind === 'system-audio' ? 'micOff' : 'camOff';
  return mute === 'hard' ? (
    <span className="pill mute-hard" title={`${what} hard-muted: not sent and NOT recorded (silence/black written)`}>
      <Icon name="ban" size={13} /> {compact ? 'Not recorded' : `${what} not recorded`}
    </span>
  ) : (
    <span className="pill mute-send" title={`${what} muted on the call – still being recorded`}>
      <Icon name={icon} size={13} /> {compact ? 'Not sent · recording' : `${what} not sent · recording`}
    </span>
  );
}
